import { FC } from "react";
import { GenericAppThemeProperty } from "../Types";
import { Box, Card, CardContent, TextField, Typography, InputAdornment } from "@material-ui/core";
import { useAppThemeContext } from "src/contexts/AppThemeContext";
import AlertMessage from "src/components/ui-components/AlertMessage";

type NumericPropertyCardProps = {
  property: GenericAppThemeProperty;
};

const NumericPropertyCard: FC<NumericPropertyCardProps> = ({ property }) => {
  const { updateGenericData } = useAppThemeContext();

  const handleChange = async (value) => {
    await updateGenericData(property.category, property.themePropertyKey, value);
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          transition: "all 0.2s ease-in-out",
          "&:hover": { boxShadow: 1, transform: "translateY(-1px)" },
        }}
      >
        <CardContent
          sx={{
            py: 1.5,
            px: 2,
            "&:last-child": { pb: 1.5 },
            "&:first-child": { pt: 1.5 },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box height="100%">
              <Typography variant="subtitle2" fontWeight={600} color="primary">
                {property.propertyLabel}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                label='Value'
                value={property.value}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  let parsedValue = Number.isInteger(property.value)
                    ? parseInt(rawValue, 10)
                    : parseFloat(rawValue);
                                
                  if (isNaN(parsedValue)) return;

                  if (property.minValue !== undefined) {
                    parsedValue = Math.max(parsedValue, Number.parseFloat(property.minValue));
                  }
                  if (property.maxValue !== undefined) {
                    parsedValue = Math.min(parsedValue, Number.parseFloat(property.maxValue));
                  }

                  if (property.minValue !== undefined && parsedValue < Number.parseFloat(property.minValue)) {
                    AlertMessage({ msg: `Value cannot be less than ${property.minValue}`, type: 'error'});
                  } else if (property.maxValue !== undefined && parsedValue > Number.parseFloat(property.maxValue)) {
                    AlertMessage({ msg: `Value cannot be more than ${property.maxValue}`, type: 'error'});
                  }
                
                  handleChange(parsedValue);
                }}
                inputProps={{
                  step: 0.05,
                  min: property.minValue,
                  max: property.maxValue,
                }}
                InputProps={{
                  ...(property.prefix && {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" color="textSecondary">
                          {property.prefix}
                        </Typography>
                      </InputAdornment>
                    ),
                  }),
                  ...(property.suffix && {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="textSecondary">
                          {property.suffix}
                        </Typography>
                      </InputAdornment>
                    ),
                  }),
                }}
                sx={{ width: 100 }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </>
  )
};

export default NumericPropertyCard;